import type { PostContentJson } from '@_/types';
import type { EditorEvents } from '@tiptap/core';
import type { UseEditorOptions } from '@tiptap/react';
import type { Constructor } from 'type-fest';
import type { BaseContent } from './lib/BaseContent.ts';
import { useDebouncedValue } from '@mantine/hooks';
import { useEditor as useTipTapEditor } from '@tiptap/react';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Placeholder } from './extensions/Placeholder.ts';

export interface UseCreateEditorOptions {
  contentConstructor: Constructor<BaseContent>;
  createDefaultContent: () => PostContentJson;
  disabled?: boolean;
  extensions?: UseEditorOptions['extensions'];
  initialValue?: PostContentJson;
  onChange?: (value: PostContentJson) => void;
  placeholder?: string;
  placeholderNodeIndex?: number;
  validator?: (value: PostContentJson) => boolean;
  validationWaitMs?: number;
}

export function useCreateEditor({
  contentConstructor,
  createDefaultContent,
  disabled,
  extensions,
  initialValue,
  onChange,
  placeholder,
  placeholderNodeIndex,
  validator,
  validationWaitMs = 200
}: UseCreateEditorOptions) {
  const initial = useMemo(
    () => initialValue || createDefaultContent(),
    [createDefaultContent, initialValue]
  );

  const [value, setValue] = useState<PostContentJson>(initial);

  const [debouncedValue] = useDebouncedValue(value, validationWaitMs, {
    leading: true
  });

  useEffect(() => {
    setValue(initial);
  }, [initial]);

  const editorExtensions = useMemo(
    () => [
      ...(extensions || []),
      Placeholder.configure({
        placeholder,
        placeholderNodeIndex
      })
    ],
    [extensions, placeholder, placeholderNodeIndex]
  );

  const editor = useTipTapEditor(
    {
      content: initial,
      editable: !disabled,
      editorProps: {
        attributes: {
          class: clsx(
            'outline-hidden [&_[data-placeholder]::before]:pointer-events-none [&_[data-placeholder]::before]:float-left [&_[data-placeholder]::before]:h-0 [&_[data-placeholder]::before]:text-gray-300 [&_[data-placeholder]::before]:content-[attr(data-placeholder)]'
          )
        }
      },
      extensions: editorExtensions,
      immediatelyRender: typeof window !== 'undefined',
      onUpdate: useCallback(
        (event: EditorEvents['update']) => setValue(event.editor.getJSON()),
        []
      ),
      shouldRerenderOnTransaction: true
    },
    [initial]
  );

  const hasChanged = useMemo<boolean>(
    () => !new contentConstructor(debouncedValue).equals(initial),
    [contentConstructor, debouncedValue, initial]
  );

  const isValid = useMemo<boolean>(
    () => (validator ? validator(debouncedValue) : true),
    [debouncedValue, validator]
  );

  useEffect(() => {
    onChange?.(value);
  }, [onChange, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  return {
    editor,
    hasChanged,
    isValid,
    value
  };
}
